import React from 'react';
import { Box, Card, CardContent, Skeleton } from '@mui/material';

type Props = {
  isDark?: boolean;
  cards?: number;
};

export const OrdersPageSkeleton = ({ isDark = true, cards = 1 }: Props) => {
  const bg = isDark ? '#1A1A1A !important' : '#E5E5DC';

  const glassBg = isDark ? '#1A1A1A' : '#E5E5DC';

  const pillBg = isDark ? '#1A1A1A' : '#E5E5DC';

  const glow = `
    0px 0px 16px 0px #5C5C5C4D inset,
    0px 0px 3px 0px #FFFFFF66 inset,
    -1px -1px 0.5px -1px #FFFFFF inset,
    1px 1px 0.5px -1px #FFFFFF66 inset,
    -1px -1px 0px -0.5px #FFFFFF80 inset,
    1px 1px 0px -0.5px #FFFFFF99 inset,
    0px 1px 8px 0px #0000001F,
    0px 0px 2px 0px #0000001A
  `;

  const cardShadow = `-2.82px -2.82px 8.49px 0px #FFFFFF4D,
          14.15px 14.15px 22.64px -0.37px #0000001A,
          -17.54px -17.54px 28.66px 0px #FFFFFF0D,
          10px 10px 28.66px -11.7px #00000033,
          1.58px 1.58px 1.7px 0px #FFFFFF inset,
          -2.07px -2.64px 4.52px -1.51px #0000004D inset`;

  return (
    <Box
      sx={{
        '&': { background: bg },
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pb: { xs: 6, sm: 8 },
        border: 0,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 956 }}>
        {/* Pills row (statuses) */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            mb: 3,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {[120, 110, 110, 120].map((w, i) => (
            <Box
              key={i}
              sx={{
                height: 40,
                px: 2,
                borderRadius: '999px',
                background: pillBg,
                boxShadow: glow,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Skeleton
                variant='text'
                width={w}
                height={18}
                sx={{
                  opacity: 0.7,
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Cards */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {Array.from({ length: cards }).map((_, idx) => (
            <Card
              key={idx}
              sx={{
                borderRadius: '32px',
                background: glassBg,
                boxShadow: cardShadow,
                overflow: 'hidden',
                position: 'relative',
                backdropFilter: 'blur(12px)',
                border: 0,
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Top row: title + status chip */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 1.2,
                  }}
                >
                  <Skeleton
                    variant='text'
                    width={220}
                    height={28}
                    sx={{
                      bgcolor: isDark
                        ? 'rgba(255,255,255,0.12)'
                        : 'rgba(0,0,0,0.08)',
                    }}
                  />

                  {/* status chip */}
                  <Box
                    sx={{
                      height: 26,
                      px: 2,
                      borderRadius: '999px',
                      background: isDark
                        ? 'rgba(0,92,173,0.65)'
                        : 'rgba(0,36,58,0.75)',
                      boxShadow: glow,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Skeleton
                      variant='text'
                      width={120}
                      height={16}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.18)',
                      }}
                    />
                  </Box>
                </Box>

                {/* Service line */}
                <Skeleton
                  variant='text'
                  width={280}
                  height={20}
                  sx={{
                    mb: 1.5,
                    bgcolor: isDark
                      ? 'rgba(255,255,255,0.10)'
                      : 'rgba(0,0,0,0.06)',
                  }}
                />

                {/* Due date + price row */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: 1.5,
                    alignItems: 'center',
                  }}
                >
                  <Skeleton
                    variant='text'
                    width={180}
                    height={22}
                    sx={{
                      bgcolor: isDark
                        ? 'rgba(255,255,255,0.10)'
                        : 'rgba(0,0,0,0.06)',
                    }}
                  />
                  <Skeleton
                    variant='text'
                    width={120}
                    height={22}
                    sx={{
                      bgcolor: isDark
                        ? 'rgba(0,255,255,0.20)'
                        : 'rgba(0,92,173,0.14)',
                    }}
                  />
                </Box>

                {/* Description lines */}
                <Box sx={{ mb: 2 }}>
                  <Skeleton
                    variant='text'
                    width='100%'
                    height={20}
                    sx={{
                      bgcolor: isDark
                        ? 'rgba(255,255,255,0.10)'
                        : 'rgba(0,0,0,0.06)',
                    }}
                  />
                  <Skeleton
                    variant='text'
                    width='92%'
                    height={20}
                    sx={{
                      bgcolor: isDark
                        ? 'rgba(255,255,255,0.10)'
                        : 'rgba(0,0,0,0.06)',
                    }}
                  />
                </Box>

                {/* Bottom row: link + button */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Skeleton
                      variant='text'
                      width={190}
                      height={22}
                      sx={{
                        bgcolor: isDark
                          ? 'rgba(255,255,255,0.10)'
                          : 'rgba(0,0,0,0.06)',
                      }}
                    />
                    <Skeleton
                      variant='text'
                      width={140}
                      height={22}
                      sx={{
                        bgcolor: isDark
                          ? 'rgba(0,255,255,0.20)'
                          : 'rgba(0,92,173,0.14)',
                      }}
                    />
                  </Box>

                  <Skeleton
                    variant='rounded'
                    width={110}
                    height={34}
                    sx={{
                      borderRadius: '999px',
                      bgcolor: isDark
                        ? 'rgba(0,92,173,0.45)'
                        : 'rgba(0,36,58,0.18)',
                      boxShadow: glow,
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Create New Task button skeleton */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Skeleton
            variant='rounded'
            width={190}
            height={44}
            sx={{
              borderRadius: '999px',
              bgcolor: isDark ? 'rgba(0,92,173,0.55)' : 'rgba(0,36,58,0.18)',
              boxShadow: glow,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
